import type { ReactNode } from 'react'
import cx from 'classnames'

import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { Button, RawButton } from '@/components/Button'

import styles from './ConsultationPendingBookingModal.module.css'
import { format } from 'date-fns'
import { CompletionToggle } from '@/components/CompletionToggle'
import { type AvatarSubject, Avatar } from '@/components/Avatar'

export interface ConsultationPendingBookingModalProps {
  showing: boolean
  onClose: () => void
  requestedLength: number
  isForTaxJob: boolean
  onForfeit: () => void
  onBook: () => void
  children: ReactNode
}

export function ConsultationPendingBookingModal({
  showing,
  onClose,
  requestedLength,
  isForTaxJob,
  onForfeit,
  onBook,
  children,
}: ConsultationPendingBookingModalProps) {
  return (
    <ModalSheet showing={showing} onRequestClose={onClose}>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent leftAligned>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            <span className={styles.title}>Select new appointment</span>
          </ModalSheetContentTitle>

          <ModalSheetContentDescription>
            <span className={styles.description}>
              {`This job requires a ${requestedLength} minute consultation call. To confirm a
              new appointment, select a new date and time to continue.`}
              {isForTaxJob && (
                <>
                  <br />
                  <br />
                  {` If you're not available at any of these times, reach out to the client
                    via the chat and ask them to update their availability.`}
                </>
              )}
            </span>
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <div className={styles.scheduledDays}>{children}</div>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          {!isForTaxJob && (
            <Button label="Forfeit Job" variant="default" onPress={onForfeit} />
          )}
          <Button
            label="Confirm Appointment"
            type="submit"
            variant="primary"
            onPress={onBook}
            testId="consultation-booking-confirm-button"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface ConsultationPendingBookingDayProps {
  title: string
  children: ReactNode
}

export function ConsultationPendingBookingDay({
  title,
  children,
}: ConsultationPendingBookingDayProps) {
  return (
    <div>
      <div className={styles.dayTitle}>{title}</div>
      {children}
    </div>
  )
}

export interface TimeSlotListItemProps {
  index: number
  startTime: Date
  endTime: Date
  isSelected: boolean
  isBooked: boolean
  onSelect: () => void
  clientAvatar: AvatarSubject
  currentMemberAvatar: AvatarSubject
}

export function TimeSlotListItem({
  index,
  startTime,
  endTime,
  isSelected,
  isBooked,
  onSelect,
  clientAvatar,
  currentMemberAvatar,
}: TimeSlotListItemProps) {
  return (
    <RawButton
      className={cx(
        styles.timeSlotItem,
        isSelected && styles.selected,
        isBooked && styles.disabled,
      )}
      testId={`time-slot-item-${startTime.getDay()}-${index}`}
      isDisabled={isBooked}
      onPress={onSelect}
    >
      <div className={styles.completionToggle}>
        <CompletionToggle
          readOnly
          checkType="tick"
          size={20}
          completed={isSelected}
          isDisabled={isBooked}
        />
      </div>
      <div className={styles.timeSlotText}>
        {isSelected && (
          <div className={styles.jobName}>
            {isBooked ? 'Unavailable' : 'Selected'}
          </div>
        )}
        {format(startTime, 'h:mm')} - {format(endTime, 'h:mmaaa')}
      </div>
      {isSelected && (
        <>
          <Avatar subject={clientAvatar} size={24} greyscale={isBooked} />
          <div className={styles.avatars}>
            <Avatar
              subject={currentMemberAvatar}
              size={24}
              greyscale={isBooked}
            />
          </div>
        </>
      )}
    </RawButton>
  )
}
